import { type ReactNode, type Ref, useCallback, useEffect, useId, useRef, useState } from 'react';
import { OverlayContainerContext } from '../../overlays/overlayContainer';
import { Button } from '../Button/Button';
import styles from './Modal.module.css';

export interface ModalProps {
    /** Controlled by the caller. Opening calls `showModal()`; closing calls `close()`. */
    readonly open: boolean;
    /** Names the dialog for a screen reader and renders as its heading. Sentence case (§9). */
    readonly title: string;
    /**
     * Fires whenever the dialog closes — the ✕, `Esc`, or a `close()` from anywhere else. The
     * caller sets `open` to false in response; a caller that does not will find the dialog gone
     * from the screen and its own state stale, which is the usual controlled-component contract.
     */
    readonly onClose: () => void;
    readonly children: ReactNode;
    /** Actions, laid out end-aligned beneath the body. */
    readonly footer?: ReactNode;
    /**
     * While true, the ✕ is disabled and `Esc` is refused (its `cancel` event is cancelled), so a
     * write in flight cannot be walked away from. The caller disables its own Cancel to match.
     */
    readonly closeDisabled?: boolean;
    /**
     * Ask before closing (F20-c FR-C6: a changed email asks whether to discard it). When set, the ✕
     * calls it instead of closing, and `Esc`'s `cancel` event is cancelled and calls it; the caller
     * closes by setting `open` to false. A `close` that arrives anyway while the caller still holds
     * `open` — Chrome's close watcher may close on a second `Esc` without a cancelable `cancel` —
     * re-opens the dialog and calls it too. Unset, the ✕ and `Esc` close as before.
     */
    readonly onCloseRequest?: (() => void) | undefined;
    /**
     * Given, the title heading takes this ref and `tabIndex={-1}`, so the caller can move focus to its
     * own heading — when what held focus inside the body has gone, as the last row of a list does.
     * Absent, the heading renders exactly as before.
     */
    readonly headingRef?: Ref<HTMLHeadingElement>;
    /**
     * `form` (the default): `layout/form-width`, for a one-column form or a confirmation — close to the shadcn
     * Dialog's width. `wide`: two list-pane widths, the New account dialog's, for a two-column form (*New lead*,
     * *New lead from inquiry*), the compose window, or a table (*Not in round*, *Price history*).
     */
    readonly width?: 'form' | 'wide';
    readonly 'data-testid'?: string;
}

/**
 * §5's Modal — a real `<dialog>` opened with `showModal()` (FS-3 FR-C2 native-first, FS-3 AC-13).
 *
 * **The whole reason for the native element is `z-index`, and it is worth being explicit about.**
 * §4 deliberately leaves modals and toasts out of the `z/*` scale: `showModal()` promotes the
 * dialog into the browser's **top layer**, which paints above every `z-index` in the document
 * whatever its value. So the perennial "my modal is behind the sticky header" bug cannot happen
 * here — the modal is not competing on `z-index` at all. The same is true in the other direction,
 * which is FS-3 AC-13's third clause: a menu opened INSIDE this dialog must itself be a `[popover]`, or
 * it renders in the ordinary stacking context and is trapped beneath its own dialog.
 *
 * **One close path, not two.** The ✕ calls the dialog's own `close()` rather than the caller's
 * `onClose` directly, and `onClose` fires from the resulting `close` EVENT. That is what makes the
 * ✕ and `Esc` indistinguishable to the caller: the browser's own cancel path already ends in
 * `close`, so routing our button through it means there is one exit and nothing to keep in step.
 *
 * **Native, and therefore not written here:** the modal backdrop, the focus trap, `Esc`, moving
 * focus into the dialog on open and restoring it to the opener on close. Every one of those is
 * `showModal()`'s own behaviour. jsdom implements none of it (see `test/setup.ts`), so those
 * properties are browser-verified evidence in the FS-3 plan rather than assertions here — the
 * tests cover what this component contributes, which is driving the API correctly.
 */
export function Modal({
    open,
    title,
    onClose,
    children,
    footer,
    closeDisabled = false,
    onCloseRequest,
    headingRef,
    width = 'form',
    'data-testid': testId,
}: ModalProps) {
    const dialogRef = useRef<HTMLDialogElement>(null);
    /*
     * The `open` the caller last asked for, written in the effect below just before it calls
     * `close()`, so the `close` handler can tell the caller's own close from one it did not ask for.
     */
    const openRef = useRef(open);
    const titleId = useId();
    /*
     * The same element as `dialogRef`, held in STATE as well so that publishing it on context
     * re-renders the consumers. A ref alone cannot: it is populated during commit without telling
     * anyone, so a Tooltip inside the modal would read `null` on the render that matters and
     * portal itself to the document — under the dialog, which is the bug this exists to prevent.
     */
    const [dialogElement, setDialogElement] = useState<HTMLDialogElement | null>(null);

    const attachDialog = useCallback((node: HTMLDialogElement | null) => {
        dialogRef.current = node;
        setDialogElement(node);
    }, []);

    useEffect(() => {
        const dialog = dialogRef.current;
        openRef.current = open;
        if (dialog === null) {
            return;
        }
        // Guarded both ways against the element's OWN state rather than against a previous prop:
        // `showModal()` on an already-open dialog throws InvalidStateError, and `close()` on a
        // closed one would emit a phantom `close` event and desync the caller.
        if (open && !dialog.open) {
            dialog.showModal();
        } else if (!open && dialog.open) {
            dialog.close();
        }
    }, [open]);

    return (
        // `onClose` is React's binding for the dialog's native `close` event — the single exit
        // described above. `onCancel` (Esc) is left to its default, which IS closing the dialog and
        // so lands here anyway — except while `closeDisabled`, when it is cancelled.
        <dialog
            aria-labelledby={titleId}
            className={styles.modal}
            data-testid={testId}
            data-width={width}
            onCancel={(event) => {
                if (closeDisabled) {
                    event.preventDefault();
                } else if (onCloseRequest !== undefined) {
                    event.preventDefault();
                    onCloseRequest();
                }
            }}
            onClose={() => {
                const dialog = dialogRef.current;
                if (onCloseRequest !== undefined && openRef.current && dialog !== null) {
                    // A close the caller did not ask for: put the dialog back and ask instead.
                    dialog.showModal();
                    onCloseRequest();
                    return;
                }
                onClose();
            }}
            ref={attachDialog}
        >
            {/*
             * The body mounts only while open. A closed <dialog> computes `display: none`, so
             * nothing is lost on screen — but an unmounted body also stops running its effects and
             * holding stale form state behind an invisible surface, which is what a caller
             * expects of a modal and what keeping it mounted would quietly break.
             */}
            {/*
             * Published only while open, and deliberately so: a closed dialog is not in the top
             * layer, so an overlay portalled into it would be `display: none` along with it.
             * `null` sends consumers back to the document default, which is correct outside a modal.
             */}
            <OverlayContainerContext.Provider value={open ? dialogElement : null}>
                {open ? (
                    <div className={styles.frame}>
                        <header className={styles.header}>
                            <h2
                                className={styles.title}
                                id={titleId}
                                {...(headingRef === undefined ? {} : { ref: headingRef, tabIndex: -1 })}
                            >
                                {title}
                            </h2>
                            <Button
                                aria-label="Close"
                                data-testid={testId === undefined ? undefined : `${testId}-close`}
                                disabled={closeDisabled}
                                icon="close"
                                onClick={() =>
                                    onCloseRequest === undefined
                                        ? dialogRef.current?.close()
                                        : onCloseRequest()
                                }
                                size="sm"
                                variant="ghost-icon"
                            />
                        </header>
                        <div className={styles.body}>{children}</div>
                        {footer === undefined ? null : <footer className={styles.footer}>{footer}</footer>}
                    </div>
                ) : null}
            </OverlayContainerContext.Provider>
        </dialog>
    );
}
